import {
	computed,
	getCurrentScope,
	nextTick,
	onScopeDispose,
	ref,
	toValue,
	watch,
	type ComputedRef,
	type MaybeRefOrGetter,
	type Ref,
} from "vue";
import type { StorageAccessor } from "@/shared/utils/storage";

/**
 * Binds a surface's search text, filter, sort or tab to a registry entry, so it
 * survives a reload and a round trip back to the same screen.
 *
 * It lives in `shared/` because the surfaces span both sections and a section may
 * not import another; its closest sibling is `useSidebarLayout`, which does the
 * same job for a sidebar's docking edge.
 *
 * Three things it deliberately does NOT do:
 *
 * - **It never re-applies.** The stored value is read once at setup (and once
 *   more whenever the scope changes). Everything that jumps a view somewhere on
 *   purpose — a canvas double-click selecting a tab, an Overview card opening a
 *   settings section — happens after that and simply wins. A restore that fought
 *   those would be a bug you could not navigate out of.
 * - **It does not trust what it reads.** The value goes through the entry's
 *   `parse`, so a sort mode we no longer ship or a tab that has closed falls back
 *   instead of rendering a view with nothing in it.
 * - **It does not decide what is worth persisting.** A momentary "find the thing
 *   I want" input — a palette filter, a picker's search — is worse for being
 *   remembered, because it opens pre-filtered. Those stay transient; see
 *   `docs/privacy-storage.md` and the plan's exclusions.
 */
export interface PersistedViewOptions {
	/**
	 * Which project / process / dataspace this value belongs to, for a scoped
	 * entry. Reactive: a view whose route param changes without remounting reads
	 * the new scope's value rather than carrying the old one across. `undefined`
	 * means "not known yet" — nothing is read or written until it is.
	 */
	scope?: MaybeRefOrGetter<string | undefined>;
	/**
	 * Text needs one; a sort or a tab does not. A discrete choice is one event and
	 * writing it immediately costs nothing, while a search box would otherwise
	 * write once per keystroke.
	 */
	debounceMs?: number;
}

export interface PersistedView<T> {
	/** Bind this where the plain `ref` used to be. */
	state: Ref<T>;
	/** Back to the entry's fallback, and the stored value removed. */
	reset: () => void;
	/**
	 * False when something is being remembered.
	 *
	 * Surfaces show a quiet "Filters restored · Clear" affordance on this, because
	 * the one failure this feature can produce is coming back to a list that is
	 * still filtered from last time, seeing three rows, and concluding the data is
	 * gone. Each surface renders its own, because each sits in a different layout:
	 * a header's search row, a sidebar field.
	 */
	isDefault: ComputedRef<boolean>;
}

/** What a search box should pass as `debounceMs`. */
export const SEARCH_DEBOUNCE_MS = 300;

export function usePersistedView<T>(
	accessor: StorageAccessor<T>,
	options: PersistedViewOptions = {},
): PersistedView<T> {
	const { scope, debounceMs = 0 } = options;
	const currentScope = () => toValue(scope);

	const state = ref(accessor.read(currentScope())) as Ref<T>;

	// Compared as stored rather than by identity, so an entry whose fallback is an
	// object still answers "is this the default" correctly.
	const defaultShape = accessor.entry.serialize(accessor.entry.fallback);
	const isDefault = computed(() => accessor.entry.serialize(state.value) === defaultShape);

	let pending: ReturnType<typeof setTimeout> | undefined;
	/** Set while this composable is the one moving `state`, so it does not write back what it just read. */
	let restoring = false;

	function writeNow(value: T) {
		pending = undefined;
		accessor.write(value, currentScope());
	}

	function adopt(value: T) {
		clearTimeout(pending);
		pending = undefined;
		restoring = true;
		state.value = value;
		// Both watchers flush in the same pre-queue, so the write watcher below has
		// already seen (and skipped) this assignment by the time nextTick runs.
		void nextTick(() => {
			restoring = false;
		});
	}

	watch(state, (value) => {
		if (restoring) return;
		if (!debounceMs) {
			writeNow(value);
			return;
		}
		clearTimeout(pending);
		pending = setTimeout(() => writeNow(value), debounceMs);
	});

	if (scope !== undefined) {
		watch(currentScope, (next) => adopt(accessor.read(next)));
	}

	function reset() {
		adopt(accessor.entry.fallback);
		accessor.clear(currentScope());
	}

	// A debounced keystroke must not be lost to navigating away from the surface
	// that made it — the timer would still fire, but the scope it reads may be gone.
	if (getCurrentScope()) {
		onScopeDispose(() => {
			if (pending === undefined) return;
			clearTimeout(pending);
			writeNow(state.value);
		});
	}

	return { state, reset, isDefault };
}
